import React, { useState, useEffect, useRef, useMemo, forwardRef } from 'react';
import { Button, PageContainer } from '../../../components/common';
import { ELEMENT_IDS } from '../../../constants/ids';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaCalendarAlt } from 'react-icons/fa';
import './selfScheduling.css';
import { format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';
import { COMPANY_CONFIG } from '../../../constants/companyConfig';
import { directApiRequest } from '../../../constants/apiRequest';
import { HTTP_METHODS } from '../../../constants/appConstants';

const SLOT_INTERVAL_MINUTES = 60;
const EASTERN_TZ = 'America/Chicago';

// ✅ Helper to return EDT/EST abbreviation
function getEasternAbbr(date) {
  return (
    new Intl.DateTimeFormat('en-US', {
      timeZone: EASTERN_TZ,
      timeZoneName: 'short',
    })
      .formatToParts(date)
      .find((part) => part.type === 'timeZoneName')?.value || ''
  );
}

const SelfSchedulingNode = ({ htmlContent, onApplyChanges, setHtmlContent, token }) => {
  const containerRef = useRef(null);

  const [availableTimes, setAvailableTimes] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showCalendar, setShowCalendar] = useState(false);
  const [hasUserSelected, setHasUserSelected] = useState(false);

  // Sync rendered HTML back to parent when user interactions change
  useEffect(() => {
    if (containerRef.current && setHtmlContent) {
      // Only sync when user makes selections, not on every render
      if (hasUserSelected || selectedSlot) {
        const currentHtml = containerRef.current.outerHTML;
        setHtmlContent(currentHtml);
      }
    }
  }, [selectedSlot, hasUserSelected]);

  // ✅ Memoize date calculations to prevent infinite re-renders
  const { today, minDate, maxDate } = useMemo(() => {
    const addWeekdays = (date, days) => {
      let result = new Date(date);
      let added = 0;
      while (added < days) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) added++;
      }
      return result;
    };

    const todayDate = new Date();
    const minDateCalc = addWeekdays(todayDate, 3);
    const maxDateCalc = addWeekdays(minDateCalc, 10);

    return {
      today: todayDate,
      minDate: minDateCalc,
      maxDate: maxDateCalc
    };
  }, []);

  // ✅ Define parseSlotDate BEFORE useEffect so it can be used
  const parseSlotDate = (slot, useEndTime = false) => {
    if (!slot || !slot.date) return null;
    
    const timeStr = useEndTime ? slot.endTime : slot.startTime;
    if (!timeStr) return null;
  
    // Convert "09.00.00" to "09:00:00"
    const [hour, minute, second] = timeStr.split('.').map(Number);
    const formattedTime = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
    
    // Create an ISO string WITHOUT timezone (local interpretation)
    const dateTimeString = `${slot.date}T${formattedTime}`;
    
    // This interprets the time as LOCAL browser time
    // We need to interpret it as Chicago time instead
    const date = new Date(dateTimeString);
    
    // Adjust for timezone difference
    const chicagoTime = new Date(date.toLocaleString('en-US', { timeZone: slot.timeZone || EASTERN_TZ }));
    const localTime = new Date(date.toLocaleString('en-US'));
    const diff = localTime.getTime() - chicagoTime.getTime();
    
    return new Date(date.getTime() + diff);
  };

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const getCustomerConfig = (domain) => {
          const configs = {
            archtop: {
              customerName: 'archtop fiber',
              departmentId: 'FIELD TECH',
              regionId: '22'
            },
            // cwf: {
            //   customerName: 'cwf',
            //   departmentId: 'TBD',
            //   regionId: 'TBD'
            // },
            default: {
              customerName: 'prtc',
              departmentId: 'I&R',
              regionId: '9'
            }
          };
          
          return configs[domain] || configs.default;
        };

        const config = getCustomerConfig(COMPANY_CONFIG.domain);

        const requestData = {
          customerName: config.customerName,
          reserveMinutes: '30',
          departmentId: config.departmentId,
          regionId: config.regionId,
          startTime: format(minDate, 'yyyy-MM-dd-HH.mm.ss'),
        };

        const api = `${process.env.REACT_APP_BASE_URL}/workflows/schedule/technician-availability`;
        const data = await directApiRequest(api, HTTP_METHODS.POST, requestData, token, {});

        const allSlots = data?.slots || [];
        
        setAvailableTimes(allSlots || []);

        // ✅ Auto-select first slot on load
        if (allSlots.length > 0) {
          const firstDate = parseSlotDate(allSlots[0]);
          if (firstDate) {
            setSelectedDate(firstDate);
            
            const firstSlot = {
              start: parseSlotDate(allSlots[0], false),
              end: parseSlotDate(allSlots[0], true),
              userId: allSlots[0].userId,
              timeZone: allSlots[0].timeZone
            };
            setSelectedSlot(firstSlot);
            
          }
        }
      } catch (error) {
        setAvailableTimes([]);
      }
    };

    fetchAvailability();
  }, [token, minDate]);

  const availableDates = useMemo(() => {
    if (!availableTimes || availableTimes.length === 0) return [];
    
    // Extract unique date strings directly from slot.date
    const dateStrings = availableTimes
      .map(slot => slot.date)
      .filter(Boolean);
    
    return [...new Set(dateStrings)];
  }, [availableTimes]);

  const slotsForSelectedDay = useMemo(() => {
    if (!selectedDate) return [];
    const dayString = format(selectedDate, 'yyyy-MM-dd');
  
    // Filter slots that match the selected date
    const daySlots = availableTimes.filter(slot => slot.date === dayString);
    
    // Convert each slot to start/end Date objects
    const allSlots = daySlots.map(slot => {
      // Combine date + startTime
      const startDate = parseSlotDate(slot, false); // false = use startTime
      
      // Combine date + endTime  
      const endDate = parseSlotDate(slot, true);    // true = use endTime
      
      return {
        start: startDate,
        end: endDate,
        userId: slot.userId,
        timeZone: slot.timeZone 
      };
    }).filter(slot => slot.start && slot.end);
  
    // Remove duplicates based on start time
    const uniqueSlots = [];
    const seen = new Set();
    allSlots.forEach((slot) => {
      const timeKey = slot.start.getTime();
      if (!seen.has(timeKey)) {
        uniqueSlots.push(slot);
        seen.add(timeKey);
      }
    });
  
    return uniqueSlots;
  }, [availableTimes, selectedDate]);

  // ✅ Availability message now JSX with EDT below
  const availabilityMessage = selectedSlot ? (
    <div>
       ✔ Our technician will arrive on{" "}
      <span className="highlight-datetime">
      {formatInTimeZone(selectedSlot.start, selectedSlot.timeZone || EASTERN_TZ, 'MMMM d, yyyy h:mm a')}{" "}
        {getEasternAbbr(selectedSlot.start)}
      </span>
      {" "}to install your service.
    </div>
  ) : (
    ''
  );
  
  const extractStyleFromElement = (element) => {
    const styleAttr = element?.getAttribute?.('style');
    if (!styleAttr) return {};
    const styleObj = {};
    styleAttr.split(';').forEach((s) => {
      const [key, value] = s.split(':').map((str) => str && str.trim());
      if (key && value) {
        const camelKey = key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
        styleObj[camelKey] = value;
      }
    });
    return styleObj;
  };

  const extractedContent = useMemo(() => {
    if (!htmlContent || htmlContent.trim() === '') {
      return {
        heading: { content: 'Schedule Installation', style: {} },
        paragraphs: [
          {
            content: `Book a convenient time to have our expert technicians install your internet connection at no additional cost.`,
            style: {},
          },
        ],
        cardTitle: { content: 'Select a Date', style: {} },
        note: {
          content:
            'Weekend installations may take longer due to high demand. For faster service, consider a weekday.',
          style: {},
        },
        buttons: {
          skip: { content: 'Skip for Now', style: {} },
          back: { content: 'Back', style: {} },
          next: { content: 'Schedule Installation', style: {} },
        },
        wrapperStyle: {},
        containerStyle: {},
        cardStyle: {},
      };
    }

    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;

    // Try to find the wrapper element with class or the first div
    const wrapperElement = tempDiv.querySelector('.selfSchedulingNode-wrapper') || 
                          tempDiv.querySelector('[class*="wrapper"]') ||
                          tempDiv.querySelector('div');
    
    // Also extract PageContainer and formCard styles
    const containerElement = tempDiv.querySelector('.selfSchedulingNode-Container') ||
                            tempDiv.querySelector('[class*="Container"]');
    
    const cardElement = tempDiv.querySelector('.selfSchedulingNode-formCard') ||
                       tempDiv.querySelector('[class*="formCard"]') ||
                       tempDiv.querySelector('[class*="card"]');

    const wrapperStyle = extractStyleFromElement(wrapperElement);
    const containerStyle = extractStyleFromElement(containerElement);
    const cardStyle = extractStyleFromElement(cardElement);
    
    console.log('Extracting styles from HTML:', {
      wrapperStyle,
      containerStyle,
      cardStyle,
      htmlContent: htmlContent.substring(0, 500) // Log first 500 chars
    });

    return {
      heading: {
        content: tempDiv.querySelector('h2')?.textContent?.trim() || 'Schedule Installation',
        style: extractStyleFromElement(tempDiv.querySelector('h2')),
      },
      paragraphs: Array.from(tempDiv.querySelectorAll('p'))
        .map((p) => ({ content: p.textContent.trim(), style: extractStyleFromElement(p) }))
        .filter((p) => p.content.length > 0),
      cardTitle: {
        content: tempDiv.querySelector('h3')?.textContent?.trim() || 'Select a Date',
        style: extractStyleFromElement(tempDiv.querySelector('h3')),
      },
      note: {
        content:
          tempDiv.querySelector('.selfSchedulingNode-note, .note')?.textContent?.trim() ||
          'Weekend installations may take longer due to high demand. For faster service, consider a weekday.',
        style: extractStyleFromElement(tempDiv.querySelector('.selfSchedulingNode-note, .note')),
      },
      buttons: {
        skip: { content: 'Skip for Now', style: {} },
        back: { content: 'Back', style: {} },
        next: { content: 'Schedule Installation', style: {} },
      },
      wrapperStyle,
      containerStyle,
      cardStyle,
    };
  }, [htmlContent]);

  const CustomDateInput = forwardRef(({ value, placeholder }, ref) => (
    <div
      className="custom-date-input"
      onClick={() => {
        setShowCalendar((prev) => !prev);
      }}
      ref={ref}
    >
      <input type="text" value={value || ''} readOnly placeholder={placeholder} />
      <FaCalendarAlt className="calendar-icon" />
    </div>
  ));
  CustomDateInput.displayName = 'CustomDateInput';

  const displayValue = selectedSlot
    ? `${formatInTimeZone(selectedSlot.start, selectedSlot.timeZone || EASTERN_TZ, 'MMMM d, yyyy h:mm a')} ${getEasternAbbr(selectedSlot.start)}`
    : selectedDate
    ? format(selectedDate, 'MMMM d, yyyy')
    : '';

  const shouldShowSlots = showCalendar && selectedDate && slotsForSelectedDay.length > 0;

  return (
    <div className="selfSchedulingNode-wrapper" style={extractedContent.wrapperStyle} ref={containerRef}>
      <PageContainer className="selfSchedulingNode-Container" style={extractedContent.containerStyle}>
        <div className="selfSchedulingNode-Heading">
          <h2 style={extractedContent.heading.style}>{extractedContent.heading.content}</h2>
          {extractedContent.paragraphs.map((p, idx) => (
            <p key={idx} style={p.style}>
              {p.content}
            </p>
          ))}
        </div>

        <div className="selfSchedulingNode-formCard" style={extractedContent.cardStyle}>
          <h3 className="selfSchedulingNode-cardTitle" style={extractedContent.cardTitle.style}>
            {extractedContent.cardTitle.content}
          </h3>

          <CustomDateInput value={displayValue} placeholder="Select a date" />

          <div className={`calendar-wrapper ${showCalendar ? 'open' : ''}`}>
            {showCalendar && (
              <DatePicker
                selected={selectedDate}
                onChange={(date) => {
                  setSelectedDate(date);
                  setSelectedSlot(null);
                }}
                minDate={minDate}
                maxDate={maxDate}
                placeholderText="Select a date"
                filterDate={(date) => {
                  const isWeekend = date.getDay() === 0 || date.getDay() === 6;
                  const isToday = date.toDateString() === today.toDateString();
                  const dateStr = format(date, 'yyyy-MM-dd');
                  const hasSlots = availableDates.includes(dateStr);
                  return !isToday && (!isWeekend || hasSlots);
                }}
              
                dayClassName={(date) => {
                  const dateStr = format(date, 'yyyy-MM-dd');
                  return availableDates.includes(dateStr) ? 'available-day' : undefined;
                }}
                
                formatWeekDay={(nameOfDay) => nameOfDay.substr(0, 3).toUpperCase()} 

                inline
              />
            )}
          </div>

          {showCalendar && selectedDate && (
            <div className="selfSchedulingNode-timeSlots">
              <p>Please select a time</p>
              {slotsForSelectedDay.length > 0 ? (
                <div className="time-slots-container">
                  {slotsForSelectedDay.map((slot, idx) => {
                    const isSelected =
                      selectedSlot && selectedSlot.start.getTime() === slot.start.getTime();
                    const slotTimeZone = slot.timeZone || EASTERN_TZ;  
                    const timeLabel = formatInTimeZone(slot.start, slotTimeZone, 'h:mm a'); 
                    const tzAbbr = getEasternAbbr(slot.start);

                    return (
                      <button
                        key={idx}
                        type="button"
                        className={`selfSchedulingNode-timeSlot ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          setSelectedSlot(slot);
                          setShowCalendar(false);
                            setHasUserSelected(true); // ✅ Add this

                        }}
                      >
                        <div className="time-label">{timeLabel}</div>
                        <div className="time-zone">{tzAbbr}</div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="no-slots-message">No slots available for this date</div>
              )}
            </div>
          )}

          {!showCalendar && availabilityMessage && hasUserSelected && (
            <div className="selfSchedulingNode-confirmationMessage">
              {availabilityMessage}
            </div>
          )}

          <div className="selfSchedulingNode-note" style={extractedContent.note.style}>
            {extractedContent.note.content}
          </div>

          <div className="selfSchedulingNode-buttonRow" style={{ marginTop: 20 }}>
            <div className="selfSchedulingNode-skipButton">
              <button
                id={ELEMENT_IDS.SKIP_BUTTON}
                className="selfSchedulingNode-skipText"
                style={{ background: 'none', border: 'none', cursor: 'pointer' }}
              >
                {extractedContent.buttons.skip.content}
              </button>
            </div>
            <div className="selfSchedulingNode-buttonGroup">
              <Button
                customId={ELEMENT_IDS.PREVIOUS_STEP_BUTTON}
                label={extractedContent.buttons.back.content}
                variant="secondary"
                style={extractedContent.buttons.back.style}
              />
              <Button
                customId={ELEMENT_IDS.NEXT_STEP_BUTTON}
                label={extractedContent.buttons.next.content}
                onClick={() => {
                  if (!selectedSlot) return;
                  const slotTimeZone = selectedSlot.timeZone || EASTERN_TZ;
                  const displayDate = `${formatInTimeZone(selectedSlot.start, slotTimeZone, 'MMMM d, yyyy h:mm a')} ${getEasternAbbr(selectedSlot.start)}`;
                  const payload = {
                    startTime: format(selectedSlot.start, 'yyyy-MM-dd-HH.mm.ss'),
                    endTime: format(selectedSlot.end, 'yyyy-MM-dd-HH.mm.ss'),
                    displayDate: displayDate,
                  };
                  onApplyChanges?.(payload);
                }}
                  disabled={!selectedSlot || !hasUserSelected} 

                style={extractedContent.buttons.next.style}
              />
            </div>
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default SelfSchedulingNode;